/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import {
  LeadCapturePage_attributeList,
  LeadCapturePage_loanCalculationResult,
} from '__generated__/graphql';
import classNames from 'classnames';
import { JSX, useEffect, useRef, useState } from 'react';
import { first } from 'utils/array';
import { Translated } from 'utils/staticText';
import { getVerticalNameFromProductUid } from '../../lib/product';
import { ITrackingProductPageBaseParameters } from '../../lib/tracking';
import { trackProductCardSeeAllProductDetails } from '../../lib/tracking/events/productCardSeeAllProductDetails';
import ProductAttribute from '../ProductAttribute';
import { ProductDialog } from '../ProductDialog';
import { ProductDialogStaticText } from '../ProductDialog/i18n';
import { CatalogProductFragment } from '../types';
import { ProductText } from './i18n';
import Article from '$/components/icons/Article';

type RewardProductProps = {
  variant: 'VARIANT_A' | 'VARIANT_B';
};

type RangeValue = {
  minimum?: number;
  maximum?: number;
};

type additionalAttributeType = {
  prefix?: string;
  suffix?: string;
  emptyValueText?: string;
  value?: number | string;
  label?: string;
  attributeType?: string;
  attributeFormat: string;
  additionalInformation?: string;
  fees?: {
    annualFlatRate?: number;
    apr?: number;
    monthlyFlatRate?: number;
  }[];
  rangeValue?: RangeValue;
};

export function Product(props: {
  product: CatalogProductFragment;
  attributes: LeadCapturePage_attributeList['attributeList'];
  staticText: Translated<typeof ProductText>;
  productDialogStaticText: Translated<typeof ProductDialogStaticText>;
  variant: RewardProductProps['variant'];
  loanCalculation?: LeadCapturePage_loanCalculationResult;
  tracking: ITrackingProductPageBaseParameters;
}): JSX.Element {
  const [showProductDetail, setShowProductDetail] = useState<boolean>(false);
  const tagLabelMobileRef = useRef<HTMLSpanElement>(null);
  const [tagLabelMobileHeight, setTagLabelMobileHeight] = useState(0);
  const heightTagLabelMobile = 30;
  const verticalCode = getVerticalNameFromProductUid(props.product?.productUid);

  const catalogueProduct = props.product;
  const {
    dialogSeeMore,
    dialogSeeLess,
    dialogRewardsTitle,
    dialogRewardsDescription,
    dialogRewardsOption,
    dialogRewardsTC,
  } = props.productDialogStaticText;

  const productCardTopBadge = props.product?.productCardTopBadge;
  const tag =
    productCardTopBadge?.__typename === 'KeyValueTextAttribute'
      ? first(first(productCardTopBadge.value)?.values)
      : undefined;

  useEffect(() => {
    const checkTagLabelHeight = () => {
      const tagLabelMobile = tagLabelMobileRef.current;
      if (tagLabelMobile) {
        const currentHeight = tagLabelMobile.clientHeight;
        setTagLabelMobileHeight(currentHeight);
      }
    };

    checkTagLabelHeight();

    const resizeListener = () => checkTagLabelHeight();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', checkTagLabelHeight);
    };
  }, []);

  return (
    <section className="mhg-alpha-rewards-lead-capture-section">
      <div className="mhg-alpha-display-1 not-italic text-[--text-primary]">
        {props.staticText.productHeading}
      </div>
      <div className="mhg-alpha-rewards-lead-capture-card">
        <div className="mhg-alpha-rewards-lead-capture-card-content">
          <div
            className={`${props.variant === 'VARIANT_A' ? 'mhg-alpha-rewards-lead-capture-card-content-heading' : 'mhg-alpha-rewards-lead-capture-card-content-heading-v2'}`}
          >
            {tag && (
              <div className="mhg-alpha-rewards-lead-capture-card-content-tag">
                <span
                  ref={tagLabelMobileRef}
                  className={`mhg-alpha-tag-font flex min-h-[--tag-size] items-center justify-center gap-[--button-gap] bg-[--highlights-green-light] text-[--colors-supplementary-green-dark] md:hidden ${tagLabelMobileHeight < heightTagLabelMobile ? 'rounded-[--tag-size] px-[--tag-padding-x] py-0 text-center' : 'rounded-[--radius-button] px-[--button-padding-regular-x-icon] py-[--spacing-padding-y-xs] text-left'}`}
                >
                  {tag}
                </span>
                <span className="mhg-alpha-tag-font hidden min-h-[--tag-size] items-center justify-center gap-[--button-gap] rounded-[--tag-size] px-[--tag-padding-x] py-0 text-center bg-[--highlights-green-light] text-[--colors-supplementary-green-dark] md:flex">
                  {tag}
                </span>
              </div>
            )}
            <div className="mhg-alpha-rewards-lead-capture-card-image-wrapper">
              <div
                className={classNames(
                  'flex flex-col items-center justify-center gap-[--spacing-gap-x] bg-[--fill-card]',
                  {
                    'p-3 rounded-[--radius-card-inner] border border-solid border-[--borders-default]':
                      verticalCode !== 'CC',
                  },
                )}
              >
                <img
                  className="h-[--provider-asset-logo-container-max-height] object-contain"
                  alt={catalogueProduct?.productName}
                  src={
                    catalogueProduct?.productImage ??
                    props.product?.productImage
                  }
                />
              </div>
              <div className="mhg-alpha-title-1 md:mhg-alpha-display-1 flex flex-1 shrink-0 basis-0 flex-col justify-center self-stretch not-italic text-[--text-primary]">
                {catalogueProduct?.productName}
              </div>
            </div>
          </div>
        </div>

        {renderProductAttributes(props.attributes, props.tracking)}

        <div className="mhg-alpha-rewards-lead-capture-card-footer">
          <button
            type="button"
            className="mhg-alpha-rewards-lead-capture-card-footer-content"
            onClick={() => {
              setShowProductDetail(true);
              trackProductCardSeeAllProductDetails(props.tracking);
            }}
          >
            <Article className="mhg-alpha-rewards-lead-capture-card-footer-content-icon" />
            <span className="mhg-alpha-rewards-lead-capture-card-footer-content-label">
              {verticalCode === 'CC'
                ? props.staticText.viewFullCardsDetails
                : props.staticText.productSeeAllDetail}
            </span>
          </button>
        </div>
      </div>
      <ProductDialog
        setOpen={setShowProductDetail}
        isOpen={showProductDetail}
        product={props.product}
        attributes={props.attributes}
        staticText={{
          dialogSeeMore,
          dialogSeeLess,
          dialogRewardsTitle,
          dialogRewardsDescription,
          dialogRewardsOption,
          dialogRewardsTC,
        }}
        tracking={props.tracking}
      />
    </section>
  );
}

export function renderProductAttributes(
  productAttributes: LeadCapturePage_attributeList['attributeList'],
  tracking: ITrackingProductPageBaseParameters,
): JSX.Element {
  const loanRateAttr = productAttributes?.find(
    (item) => item?.attributeType === 'LOAN_RATES',
  ) as additionalAttributeType;

  const highlightedAttributes: additionalAttributeType[] = productAttributes
    ?.filter((item) => item?.isHighlighted)
    .map((item) => {
      if (item?.attributeType === 'APR') {
        return {
          ...item,
          value: first(loanRateAttr?.fees)?.annualFlatRate,
        };
      }
      if (item?.attributeType === 'MONTHLY_FLAT_RATE') {
        return {
          ...item,
          value: first(loanRateAttr?.fees)?.monthlyFlatRate,
        };
      }
      if (item?.attributeType === 'ANNUALLY_FLAT_RATE') {
        return {
          ...item,
          value: first(loanRateAttr?.fees)?.annualFlatRate,
        };
      }
      return { ...item };
    }) as additionalAttributeType[];

  return (
    <>
      <div className="mhg-alpha-rewards-lead-capture-card-attributes">
        {highlightedAttributes
          ?.filter((_, index) => index % 2 === 0)
          .map((attr) =>
            attr ? (
              <ProductAttribute
                key={attr.attributeType}
                label={attr.label}
                {...getAttributeDisplay(attr)}
                tooltipContent={attr.additionalInformation}
                classNameWrapper="col-1 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
                tracking={tracking}
              />
            ) : null,
          )}
        {highlightedAttributes
          ?.filter((_, index) => index % 2 !== 0)
          .map((attr) =>
            attr ? (
              <ProductAttribute
                key={attr.attributeType}
                label={attr.label}
                {...getAttributeDisplay(attr)}
                tooltipContent={attr.additionalInformation}
                classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
                tracking={tracking}
              />
            ) : null,
          )}
      </div>

      {/* product attributes for mobile view */}
      <div className="mhg-alpha-rewards-lead-capture-card-attributes-v2">
        <div className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch">
          {highlightedAttributes?.map((attr) =>
            attr ? (
              <ProductAttribute
                key={attr.attributeType}
                label={attr.label}
                {...getAttributeDisplay(attr)}
                tooltipContent={attr.additionalInformation}
                classNameWrapper="flex items-start self-stretch gap-[--spacing-gap-x]"
                classNameLabel="w-[--provider-asset-size-1]"
                tracking={tracking}
              />
            ) : null,
          )}
        </div>
      </div>
    </>
  );
}

// A NUMBER attribute without a numeric value (null / undefined / '' - e.g. a
// loan rate the product has no fee for) shows the CMS `emptyValueText` instead,
// without prefix/suffix, mirroring the CMS attr helper.
const isEmptyNumberValue = (value: additionalAttributeType['value']) =>
  value === undefined ||
  value === null ||
  String(value).trim() === '' ||
  Number.isNaN(Number(value));

const getAttributeDisplay = (
  attribute: additionalAttributeType,
): { value: string | number; prefix: string; suffix: string } => {
  if (
    attribute.attributeFormat === 'NUMBER' &&
    isEmptyNumberValue(attribute.value)
  ) {
    return { value: attribute.emptyValueText ?? '', prefix: '', suffix: '' };
  }
  return {
    value: getAttributeValue(attribute),
    prefix: prefixAttribute(attribute.prefix),
    suffix: suffixAttribute(attribute.suffix),
  };
};

const getAttributeValue = (
  attribute: additionalAttributeType,
): string | number => {
  switch (attribute.attributeFormat) {
    case 'NUMBER':
      return Number(attribute.value).toLocaleString('en-SG', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    case 'NUMBER_RANGE':
      return formatRangeValue(attribute?.rangeValue);
    default:
      return attribute.value ?? '';
  }
};

const formatRangeValue = (rangeValue?: RangeValue | null): string => {
  if (!rangeValue) return '';

  const { minimum, maximum } = rangeValue;

  if (minimum !== undefined && maximum !== undefined) {
    return `${minimum} - ${maximum}`;
  }
  return minimum !== undefined
    ? `${minimum}`
    : maximum !== undefined
      ? `${maximum}`
      : '';
};

const prefixAttribute = (prefixText: string | undefined): string => {
  if (!prefixText) return '';
  const isCurrencyPrefix = ['S$', 'HK$', '₱'];
  return isCurrencyPrefix.includes(prefixText) ? prefixText : `${prefixText} `;
};

const suffixAttribute = (suffixText: string | undefined): string => {
  if (!suffixText) return '';
  return suffixText.startsWith('%') ? suffixText : ` ${suffixText}`;
};
